import React, { useState, useMemo } from "react";
import { useCompanyParties } from "../hooks/useCompanyParties.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { CustomerCard } from "../components/CustomerCard.jsx";
import { CustomerForm } from "../components/CustomerForm.jsx";
import { ShippingAddressModal } from "../components/ShippingAddressModal.jsx";
import { CustomerPricingModal } from "../components/CustomerPricingModal.jsx";
import { CustomerLedgerModal } from "../components/CustomerLedgerModal.jsx";
import { PaymentTallyModal } from "../../invoices/components/PaymentTallyModal.jsx";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { useListView } from "../../../shared/hooks/useListView.js";
import { DataTable } from "../../../shared/components/DataTable.jsx";
import { ViewToggle, StickyFooter } from "../../../shared/components/ListChrome.jsx";
import { customerColumns } from "../components/customerColumns.jsx";

export function CustomersPage() {
  // Only the working company's parties (header), each with its due for that company
  const {
    customers: companyParties,
    dueOf,
    loading,
    error,
    addCustomer,
    editCustomer,
    removeCustomer,
    fetchAddresses,
    addAddress,
    removeAddress,
    saveCustomerPrices
  } = useCompanyParties();
  const { activeCompany } = useActiveCompany();
  const customers = useMemo(() => companyParties.map((c) => ({ ...c, companyDue: dueOf(c) })), [companyParties, dueOf]);

  const [searchTerm, setSearchTerm] = useState("");
  const [partyTypeFilter, setPartyTypeFilter] = useState("ALL"); // 'ALL' | 'CUSTOMERS' | 'VENDORS'
  const [activeFilter, setActiveFilter] = useState("ALL"); // 'ALL' | 'BALANCE' | 'GST'
  const [isCustomerSheetOpen, setIsCustomerSheetOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [addressModalCustomer, setAddressModalCustomer] = useState(null);
  const [pricingModalCustomer, setPricingModalCustomer] = useState(null);
  const [ledgerView, setLedgerView] = useState(null); // { customer, tab } for the ledger sheet
  const [tallyModalCustomer, setTallyModalCustomer] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { confirmDanger } = useDangerConfirm();

  // Counts
  const counts = useMemo(() => {
    let custCount = 0;
    let vendCount = 0;
    customers.forEach((c) => {
      const type = c.partyType || "customer";
      if (type === "customer" || type === "both") custCount++;
      if (type === "vendor" || type === "both") vendCount++;
    });
    return {
      all: customers.length,
      customers: custCount,
      vendors: vendCount
    };
  }, [customers]);

  // Filter and search
  const filteredCustomers = useMemo(() => {
    return customers.filter((cust) => {
      // 1. Party classification filter
      const type = cust.partyType || "customer";
      if (partyTypeFilter === "CUSTOMERS" && type !== "customer" && type !== "both") {
        return false;
      }
      if (partyTypeFilter === "VENDORS" && type !== "vendor" && type !== "both") {
        return false;
      }

      // 2. Search filter
      const term = searchTerm.toLowerCase().trim();
      const name = (cust.name || "").toLowerCase();
      const gstin = (cust.gstin || "").toLowerCase();
      const phone = (cust.mobile || "").toLowerCase();
      const city = (cust.city || "").toLowerCase();

      const matchesSearch = !term || name.includes(term) || gstin.includes(term) || phone.includes(term) || city.includes(term);
      if (!matchesSearch) return false;

      // 3. Secondary status filters
      if (activeFilter === "BALANCE") {
        return Number(cust.companyDue || 0) > 0;
      }
      if (activeFilter === "GST") {
        return !!cust.gstin && cust.gstin.trim().length > 0;
      }

      return true;
    });
  }, [customers, searchTerm, partyTypeFilter, activeFilter]);

  const handleOpenCreate = () => {
    setEditingCustomer(null);
    setIsCustomerSheetOpen(true);
  };

  const handleOpenEdit = (customer) => {
    setEditingCustomer(customer);
    setIsCustomerSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsCustomerSheetOpen(false);
    setEditingCustomer(null);
  };

  const handleFormSubmit = async (formData) => {
    setFormSubmitting(true);
    try {
      if (editingCustomer) {
        await editCustomer(editingCustomer.id, formData);
      } else {
        await addCustomer(formData);
      }
      handleCloseSheet();
    } finally {
      setFormSubmitting(false);
    }
  };

  // Dynamic header text & button
  const pageTitle =
    partyTypeFilter === "VENDORS"
      ? "Vendors & Suppliers"
      : partyTypeFilter === "CUSTOMERS"
      ? "Customers & Clients"
      : "Customers & Parties";

  const addBtnLabel =
    partyTypeFilter === "VENDORS"
      ? "Add Vendor"
      : partyTypeFilter === "CUSTOMERS"
      ? "Add Customer"
      : "Add Party";

  const listView = useListView("customers", { onNew: handleOpenCreate });
  const columns = useMemo(() => customerColumns({
    onSettle: setTallyModalCustomer,
    onInvoices: (c) => setLedgerView({ customer: c, tab: "invoices" }),
    onRates: setPricingModalCustomer,
    onAddresses: setAddressModalCustomer,
    onEdit: handleOpenEdit,
    onDelete: async (c) => {
      const typeText = c.partyType === "vendor" ? "vendor" : c.partyType === "both" ? "party" : "customer";
      if (await confirmDanger({ title: `Delete ${typeText}?`, message: `Are you sure you want to delete ${typeText} "${c.name}"?` })) {
        removeCustomer(c.id);
      }
    }
  }), [confirmDanger, removeCustomer]);

  const balanceTotals = useMemo(() => filteredCustomers.reduce((totals, c) => {
    const balance = Number(c.companyDue || 0);
    if (balance > 0) totals[c.partyType === "vendor" ? "payable" : "receivable"] += balance;
    return totals;
  }, { receivable: 0, payable: 0 }), [filteredCustomers]);

  const defaultPartyTypeForForm = partyTypeFilter === "VENDORS" ? "vendor" : "customer";

  return (
    <div className="customers-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.5rem", width: "100%" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "700" }}>{pageTitle}{activeCompany ? <span style={{ fontWeight: 500, color: "var(--text-dim)", fontSize: "0.95rem" }}> · {activeCompany.name}</span> : null}</h2>
          <p style={{ fontSize: "0.8rem", color: "var(--text-dim)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {filteredCustomers.length} {filteredCustomers.length === 1 ? (partyTypeFilter === "VENDORS" ? "vendor" : "party") : (partyTypeFilter === "VENDORS" ? "vendors" : "parties")} listed
          </p>
        </div>
        <Button size="sm" variant="primary" onClick={handleOpenCreate} style={{ flexShrink: 0 }}>
          {addBtnLabel}
        </Button>
      </div>

      {/* Primary Party Classification Segmented Bar */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.4rem", marginBottom: "0.75rem" }}>
        <button
          type="button"
          className={`btn btn-sm ${partyTypeFilter === "ALL" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setPartyTypeFilter("ALL")}
          style={{ fontSize: "0.78rem", padding: "0.35rem 0.2rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
        >
          <span>👥 All</span>
          <span style={{ opacity: 0.85, fontSize: "0.72rem" }}>({counts.all})</span>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${partyTypeFilter === "CUSTOMERS" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setPartyTypeFilter("CUSTOMERS")}
          style={{ fontSize: "0.78rem", padding: "0.35rem 0.2rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
        >
          <span>🛒 Customers</span>
          <span style={{ opacity: 0.85, fontSize: "0.72rem" }}>({counts.customers})</span>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${partyTypeFilter === "VENDORS" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setPartyTypeFilter("VENDORS")}
          style={{ fontSize: "0.78rem", padding: "0.35rem 0.2rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
        >
          <span>🏭 Vendors</span>
          <span style={{ opacity: 0.85, fontSize: "0.72rem" }}>({counts.vendors})</span>
        </button>
      </div>

      {/* Filter pills + search (one sticky toolbar row on desktop) */}
      <div className="list-toolbar">
        <div className="filter-pills-row" style={{ marginBottom: "0.75rem" }}>
          <button
            type="button"
            className={`filter-pill ${activeFilter === "ALL" ? "active" : ""}`}
            onClick={() => setActiveFilter("ALL")}
          >
            All
          </button>
          <button
            type="button"
            className={`filter-pill ${activeFilter === "BALANCE" ? "active" : ""}`}
            onClick={() => setActiveFilter("BALANCE")}
          >
            With Balance
          </button>
          <button
            type="button"
            className={`filter-pill ${activeFilter === "GST" ? "active" : ""}`}
            onClick={() => setActiveFilter("GST")}
          >
            GST Registered
          </button>
        </div>

        {/* Search Input */}
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by party name, phone, city, or GSTIN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <ViewToggle view={listView.view} onChange={listView.setView} />
      </div>

      {loading && (
        <div className="state-box">
          <div className="state-icon">⏳</div>
          <div className="state-title">Loading...</div>
        </div>
      )}

      {error && (
        <div className="state-box">
          <div className="state-icon">⚠️</div>
          <div className="state-title">Error Loading Parties</div>
          <div className="state-desc">{error}</div>
        </div>
      )}

      {!loading && !error && filteredCustomers.length === 0 && (
        <div className="state-box">
          <div className="state-icon">👥</div>
          <div className="state-title">
            {partyTypeFilter === "VENDORS" ? "No vendors found" : partyTypeFilter === "CUSTOMERS" ? "No customers found" : "No parties found"}
          </div>
          <div className="state-desc">
            {searchTerm ? "No party matches your search filter." : (partyTypeFilter === "VENDORS" ? "Add your first vendor or supplier." : "Add your first client or party to start billing.")}
          </div>
          <Button variant="primary" onClick={handleOpenCreate}>
            {addBtnLabel}
          </Button>
        </div>
      )}

      {listView.showTable ? (
        <DataTable
          columns={columns}
          rows={filteredCustomers}
          onRowClick={(c) => setLedgerView({ customer: c, tab: "ledger" })}
          storageKey="customers"
          noun="parties"
          resetKey={`${partyTypeFilter}|${activeFilter}|${searchTerm}`}
        />
      ) : (
        <div className="customers-list card-grid">
          {filteredCustomers.map((cust) => (
            <CustomerCard
              key={cust.id}
              customer={cust}
              onEdit={handleOpenEdit}
              onDelete={removeCustomer}
              onManageAddresses={setAddressModalCustomer}
              onConfigurePrices={setPricingModalCustomer}
              onViewInvoices={(c) => setLedgerView({ customer: c, tab: "invoices" })}
              onViewLedger={(c, tab) => setLedgerView({ customer: c, tab })}
              onTallyPayment={setTallyModalCustomer}
            />
          ))}
        </div>
      )}

      <StickyFooter
        shown={filteredCustomers.length}
        total={counts.all}
        noun="parties"
        chips={[
          { label: "To receive", value: `₹${balanceTotals.receivable.toLocaleString("en-IN")}`, tone: balanceTotals.receivable > 0 ? "warning" : "success" },
          { label: "To pay", value: `₹${balanceTotals.payable.toLocaleString("en-IN")}`, tone: balanceTotals.payable > 0 ? "danger" : "success" }
        ]}
      />

      {/* Floating Action Button */}
      <button
        type="button"
        className="fab"
        onClick={handleOpenCreate}
        aria-label={addBtnLabel}
      >
        +
      </button>

      {/* Add / Edit Customer / Vendor Sheet */}
      <BottomSheet
        isOpen={isCustomerSheetOpen}
        onClose={handleCloseSheet}
        title={
          editingCustomer
            ? `Edit ${editingCustomer.partyType === "vendor" ? "Vendor" : editingCustomer.partyType === "both" ? "Party" : "Customer"}`
            : (partyTypeFilter === "VENDORS" ? "New Vendor / Supplier" : "New Customer / Party")
        }
      >
        <CustomerForm
          initialData={editingCustomer}
          defaultPartyType={defaultPartyTypeForForm}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseSheet}
          loading={formSubmitting}
        />
      </BottomSheet>

      {/* Shipping Address Subcollection Modal */}
      <ShippingAddressModal
        isOpen={!!addressModalCustomer}
        onClose={() => setAddressModalCustomer(null)}
        customer={addressModalCustomer}
        onFetchAddresses={fetchAddresses}
        onAddAddress={addAddress}
        onRemoveAddress={removeAddress}
      />

      {/* Customer Item Pricing Configuration Modal */}
      <CustomerPricingModal
        isOpen={!!pricingModalCustomer}
        onClose={() => setPricingModalCustomer(null)}
        customer={pricingModalCustomer}
        onSavePrices={saveCustomerPrices}
      />

      {/* Customer Invoices List / Detail / Print Modal */}
      <CustomerLedgerModal
        isOpen={!!ledgerView}
        onClose={() => setLedgerView(null)}
        customer={ledgerView?.customer}
        initialTab={ledgerView?.tab || "ledger"}
      />

      {/* Customer Payment Tally Modal */}
      <PaymentTallyModal
        isOpen={!!tallyModalCustomer}
        onClose={() => setTallyModalCustomer(null)}
        preselectedCustomer={tallyModalCustomer}
      />
    </div>
  );
}

export default CustomersPage;
